import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import ExplicitConfirmationDialog from 'common/dialog-explicit-confirmation/DialogExplicitConfirmation';
import { useParams } from 'react-router-dom';
import { useDeleteLoadMutation, useViewLoadsSummaryByDriverQuery } from '../loadsEndpoints';

const DeleteDialog = ({ driver, load, onCloseDialog }) => {
  const { viewById } = useParams();

  const [deleteLoad, { isSuccess }] = useDeleteLoadMutation();

  const { driverQuery } = useViewLoadsSummaryByDriverQuery(undefined, {
    selectFromResult: ({ data }) => ({
      driverQuery: data?.find(R.propEq('driverId', viewById)),
    }),
    skip: R.not(R.isNil(driver)),
  });
  const driverDetails = driver ?? driverQuery;

  const handleDelete = () => {
    deleteLoad({ load });
  };

  useEffect(() => {
    if (isSuccess) onCloseDialog();
  }, [isSuccess, onCloseDialog]);

  const draftDialog = (
    <Dialog
      maxWidth="xs"
      open
      slotProps={{
        paper: { elevation: 10 },
      }}
      sx={{ '& .MuiDialog-paper': { height: 200, width: 380 } }}
    >
      <DialogTitle sx={{ mt: 1 }}>Would you like to delete this load?</DialogTitle>
      <Typography sx={{ mb: 3, mx: 3 }}>
        If you delete the load, you will not be able to complete it later.
      </Typography>
      <DialogActions>
        <Button autoFocus onClick={onCloseDialog} sx={{ mr: 1 }} variant="outlined">
          Back
        </Button>
        <Button onClick={handleDelete} sx={{ mr: 1 }}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );

  const confirmationDialog = (
    <ExplicitConfirmationDialog
      confirmationMessage="By deleting this load, it will be removed from pending loads and the fleet driver's schedule."
      confirmationValue="DELETE"
      dialogOpen
      handleCloseDialog={onCloseDialog}
      onConfirm={() => {
        handleDelete();
      }}
      title="Would you like to delete this load?"
      warningList={[
        `This load has been scheduled for ${driverDetails.firstName} ${driverDetails.lastName}.`,
      ]}
    />
  );

  return R.either(R.equals('Draft'), R.equals('DRAFT'))(load.status)
    ? draftDialog
    : confirmationDialog;
};

DeleteDialog.propTypes = {
  driver: PropTypes.shape({}),
  load: PropTypes.shape({
    status: PropTypes.string,
  }).isRequired,
  onCloseDialog: PropTypes.func.isRequired,
};
DeleteDialog.defaultProps = {
  driver: undefined,
};

export default DeleteDialog;
